// U3 "Start easily" on a card, under its title: the ask in one line with roughly how long it
// takes, a "Make time tomorrow at 10?" offer for a card that needs focus time (arm → confirm,
// through the gateway's gcal.focus approval), and, once a block is made, where the card waits.
// Nothing here blocks the card from painting: the focus suggestion loads on its own.
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, ShieldCheck } from "lucide-react";
import { DEFAULT_FOCUS_DURATION } from "@shared/agenda";
import type { CardSummary, CardView } from "@shared/api";
import { effortWords } from "@shared/startEasily";
import { slotWords } from "@web/day/dayWords";
import { addFocusBlock } from "@web/day/focusOps";
import { makeTimeStore } from "@web/day/makeTimeStore";
import { isApiError } from "@web/lib/api";
import { dayApi } from "@web/lib/dayApi";
import { useOwnerTimeZone, useShowEstimates } from "@web/lib/settingsApi";
import { useNow } from "@web/lib/useNow";
import { Button } from "@web/ui/Button";
import { CONFIRM_GUARD_MS } from "../actions";
import { isToReview } from "../selectors";
import { askLineText, askLineWords, focusSlotWords } from "../startWords";

/** "Sam asks you to confirm Thursday · by tomorrow (“by Wed EOD”) · about 1 min". */
export function AskLine({ card }: { card: CardSummary }) {
  const now = useNow(60_000);
  const zone = useOwnerTimeZone();
  const show = useShowEstimates();
  if (!isToReview(card)) return null;
  const words = askLineWords(card.ask, now, zone);
  const estimate = show ? effortWords(card.effort) : null;
  if (!words && !estimate) return null;
  return (
    <p data-testid="ask-line" className="mt-1.5 text-[15px] leading-snug text-ink-2">
      {words ? (
        <>
          {words.text ? <span className="text-ink">{words.text}</span> : null}
          {words.text && words.when ? <span className="text-muted"> · </span> : null}
          {words.when ? (
            <span>
              {words.when}
              {words.quote ? <span className="text-muted"> (“{words.quote}”)</span> : null}
            </span>
          ) : null}
          <span className="sr-only">{askLineText(words)}</span>
        </>
      ) : null}
      {estimate ? (
        <span data-testid="effort" className="text-sm whitespace-nowrap text-muted">
          {words ? " · " : ""}
          {estimate}
        </span>
      ) : null}
    </p>
  );
}

function suggestionProblem(error: unknown): string {
  if (isApiError(error, "stale")) return "That time just filled up. Pick another one.";
  if (isApiError(error, "invalid_request"))
    return error.serverMessage ?? "That time can’t be used. Pick another one.";
  return "Couldn’t reach the server. Nothing was added.";
}

/**
 * "Needs focus time. Make time tomorrow at 10?" The first press only arms; a second, separate
 * press adds the block (the gateway binds and re-checks it), and the card moves to Lined up.
 */
export function FocusTimeOffer({ card, actionable }: { card: CardView; actionable: boolean }) {
  const queryClient = useQueryClient();
  const now = useNow(60_000);
  const offered = actionable && isToReview(card) && card.effort === "long" && !card.updatePending;
  const suggestion = useQuery({
    queryKey: ["focusSuggest", card.id],
    queryFn: ({ signal }) =>
      dayApi.focusSuggest(
        { target: { kind: "card", id: card.id }, durationMin: DEFAULT_FOCUS_DURATION },
        signal,
      ),
    enabled: offered,
    staleTime: 60_000,
    retry: false,
  });
  const [armedAt, setArmedAt] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (armedAt) confirmRef.current?.focus({ preventScroll: true });
  }, [armedAt]);
  if (!offered) return null;

  const data = suggestion.data;
  const slot = data?.connected ? data.slot : null;
  const zone = data?.timeZone;
  const otherTimes = () =>
    makeTimeStore.open({ target: { kind: "card", id: card.id }, title: card.title, lineUp: true });

  const confirm = async () => {
    if (!slot || !data || busy || !armedAt || Date.now() - armedAt < CONFIRM_GUARD_MS) return;
    setBusy(true);
    try {
      await addFocusBlock(
        queryClient,
        {
          target: { kind: "card", id: card.id },
          slot: { start: slot.start, end: slot.end },
          title: data.title,
          lineUp: true,
        },
        slotWords(slot, Date.now(), data.timeZone).label,
      );
    } catch (error) {
      setProblem(suggestionProblem(error));
      void suggestion.refetch();
    } finally {
      setBusy(false);
      setArmedAt(null);
    }
  };

  if (armedAt && slot && data) {
    const words = slotWords(slot, now, data.timeZone);
    return (
      <div
        role="group"
        aria-label="Confirm: add focus time"
        data-testid="focus-offer-armed"
        className="mt-2 flex flex-col gap-2 rounded-lg border border-accent/40 bg-accent-soft/60 px-3 py-2.5 text-sm"
      >
        <p className="flex items-center gap-2 font-medium text-ink">
          <ShieldCheck aria-hidden className="size-4 text-accent-text" />
          Add focus time {words.label} to {data.email ?? "your calendar"}?
        </p>
        <p className="text-muted">
          Private, no guests. The card waits under Lined up until then. You can undo it.
        </p>
        <div className="flex gap-2">
          <Button
            ref={confirmRef}
            size="sm"
            variant="primary"
            busy={busy}
            onKeyDown={(e) => {
              if (e.repeat) e.preventDefault();
            }}
            onClick={() => void confirm()}
            data-testid="focus-offer-confirm"
          >
            {busy ? "Adding…" : "Add to calendar"}
          </Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => setArmedAt(null)}>
            Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="focus-offer"
      className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-ink-2"
    >
      <CalendarClock aria-hidden className="size-4 text-muted" />
      <span>
        {slot
          ? `Make time for it ${focusSlotWords(slot.start, now, zone)}?`
          : "This needs focus time."}
      </span>
      {slot ? (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            setProblem(null);
            setArmedAt(Date.now());
          }}
        >
          Make time
        </Button>
      ) : null}
      <button
        type="button"
        className="text-accent-text underline underline-offset-2"
        onClick={otherTimes}
      >
        {slot ? "Other times" : "Find a time"}
      </button>
      {problem ? (
        <span role="status" className="basis-full text-note-ink">
          {problem}
        </span>
      ) : null}
    </div>
  );
}

/** Where a card made time for waits: "Focus time tomorrow at 10 AM · Open in Calendar". */
export function LinedUpNote({ card }: { card: CardSummary }) {
  const now = useNow(60_000);
  const zone = useOwnerTimeZone();
  const block = card.focusBlock;
  if (!block) return null;
  const waiting = card.status === "done" && card.disposition === "snoozed";
  return (
    <p data-testid="lined-up-note" className="mt-1.5 flex flex-wrap items-center gap-x-2 text-sm">
      <CalendarClock aria-hidden className="size-4 text-muted" />
      <span className="text-ink-2">
        {waiting ? "Lined up for focus time" : "Focus time"}{" "}
        {focusSlotWords(block.start, now, zone ?? block.tz)}
      </span>
      <a
        href={block.link}
        target="_blank"
        rel="noopener noreferrer"
        className="text-accent-text underline underline-offset-2"
      >
        Open in Calendar
      </a>
    </p>
  );
}

/** Everything U3 adds under a card's title, in one place. */
export function CardStartLines({
  summary,
  card,
  actionable,
}: {
  summary: CardSummary;
  card: CardView | undefined;
  actionable: boolean;
}) {
  return (
    <>
      <AskLine card={card ?? summary} />
      <LinedUpNote card={card ?? summary} />
      {card ? <FocusTimeOffer card={card} actionable={actionable} /> : null}
    </>
  );
}
